import { makeAutoObservable, runInAction } from 'mobx';

import { StockistIssue } from "../../DTOs/StockistIssue";
import { PromotionSummary } from "../../DTOs/PromotionSummary";
import { stockiestPortalController } from "../StockiestPortalController";
import { StockistQuantityDiscountModelDTO } from '../../DTOs/Discount/StockistQuantityDiscountModelDTO';
import { MAX_ISSUES_AVAILABLE } from "../../DTOs/Stock";
import { issueAjaxController } from "../../Ajax/IssueAjaxController";

export class OrderIssueController {
  private weight: number = 0;
  private price: number = 0;
  private isUpcoming: boolean = false;
  private lastKnownInBasket: number = -1;

  issuesAvailable: number = 0;
  issuePrice: number = 0;
  issueWeightKilograms: number = 0;
  orderedAmount: number = 0;
  priceInputError: boolean = false;
  mostSpecificDiscounts: StockistQuantityDiscountModelDTO[] = [];
  promotionSummary: PromotionSummary | null = null;
  isLoadingPromotionSummary: boolean = false;
  isFreebieEligible: boolean | null = null;

  get maxOrderable(): number {
    if (this.isUpcoming) return MAX_ISSUES_AVAILABLE;
    return this.issuesAvailable > 0 ? this.issuesAvailable : 0;
  }

  get freebiesFromCurrentOrder(): number {
    if (!this.promotionSummary) return 0;
    const freebieEligibleQty = this.promotionSummary.freebieEligibleQuantity;
    const freebiesEarned = this.promotionSummary.freebiesEarned;
    const newEligibleTotal = freebieEligibleQty + this.orderedAmount;
    const newFreebiesEarned = Math.floor(newEligibleTotal / 10);
    return Math.max(0, newFreebiesEarned - freebiesEarned);
  }

  updateStore(issue: StockistIssue) {
    runInAction(() => {
      this.issuesAvailable = issue.stock.issuesAvailableInTotal;
      this.issuePrice = issue.price;
      this.issueWeightKilograms = issue.weightKilograms;

      const currentInBasket = issue.stock.inBasket;
      if (currentInBasket !== this.lastKnownInBasket) {
        this.lastKnownInBasket = currentInBasket;
        // Default to 1 copy when the issue isn't in the basket yet, so the
        // dialog opens with a sensible pre-selected quantity and the slider
        // starts at the correct (non-zero) position.
        this.orderedAmount = currentInBasket > 0
          ? Math.min(currentInBasket, this.maxOrderable)
          : Math.min(1, this.maxOrderable);
        this.price = this.orderedAmount * this.effectivePricePerIssue;
        this.weight = this.orderedAmount * this.issueWeightKilograms;
      }
    });
  }

  get sliderPercentage() {
    return (this.orderedAmount / this.maxOrderable) * 100 || 0;
  }

  onCopiesChange(amount: number) {
    runInAction(() => {
      if (isNaN(amount)) {
        this.orderedAmount = 0;
        return;
      }
      const clamped = Math.max(0, Math.min(amount, this.maxOrderable));
      this.orderedAmount = clamped;
      this.price = this.orderedAmount * this.effectivePricePerIssue;
      this.weight = this.orderedAmount * this.issueWeightKilograms;
    });
  }

  onSliderChange(event: React.ChangeEvent<HTMLInputElement>) {
    runInAction(() => {
      this.price = this.weight = NaN;
      this.priceInputError = false;

      const percentageAsItems = Math.round(
        (+event.target.value * this.maxOrderable) / 100
      );

      this.orderedAmount =
        percentageAsItems <= 1
          ? 1
          : percentageAsItems > this.maxOrderable
            ? this.maxOrderable
            : percentageAsItems;
    });
  }

  get orderedWeightKilograms() {
    if (!isNaN(this.weight)) return this.weight;
    return this.orderedAmount * this.issueWeightKilograms;
  }

  get effectivePricePerIssue(): number {
    const currentOrderQty = this.orderedAmount;
    if (!this.mostSpecificDiscounts?.length || currentOrderQty <= 0) return this.issuePrice;

    const totalQty = currentOrderQty + (this.promotionSummary?.totalPurchased ?? 0);

    const applicable = this.mostSpecificDiscounts
      .filter((d) => totalQty >= d.quantityFrom)
      .sort((a, b) => b.quantityFrom - a.quantityFrom);

    const best = applicable[0];
    if (!best) return this.issuePrice;

    const discountMultiplier = 1 - best.discountPercentage / 100;
    return +(this.issuePrice * discountMultiplier).toFixed(2);
  }

  get orderedPrice() {
    if (!isNaN(this.price)) return this.price.toFixed(2);
    return (this.orderedAmount * this.effectivePricePerIssue).toFixed(2);
  }

  async fetchPromotionSummary(issueId: number) {
    runInAction(() => { this.isLoadingPromotionSummary = true; });
    try {
      const summary = await issueAjaxController.getPromotionSummary(issueId);
      runInAction(() => {
        this.promotionSummary = summary;
        this.isLoadingPromotionSummary = false;
      });
    } catch {
      runInAction(() => { this.isLoadingPromotionSummary = false; });
    }
  }

  async fetchFreebieEligibility(publisherId: number) {
    try {
      const eligible = await issueAjaxController.getPublisherFreebieEligibility(publisherId);
      runInAction(() => { this.isFreebieEligible = eligible; });
    } catch {
      // leave as null — freebie UI stays hidden
    }
  }

  async confirmOrder(issue: StockistIssue) {
    if (this.orderedAmount <= 0) return;

    await stockiestPortalController.addOrderedProduct(issue, this.orderedAmount);

    runInAction(() => {
      this.issuesAvailable = issue.stock.issuesAvailableInTotal;
      this.orderedAmount = 0;
    });
  }

  constructor(issue: StockistIssue) {
    makeAutoObservable(this);

    const now = new Date();
    const onSale = new Date(issue.issueOnSale);
    this.isUpcoming = onSale > now;

    // Set once here — discounts don't change for a given issue during a session.
    // Keeping this out of updateStore prevents an infinite render loop: updateStore
    // is called on every render (via getOrCreateStore), and assigning a new array
    // reference each time would continuously trigger MobX reactions in any observer
    // that reads orderedPrice → effectivePricePerIssue → mostSpecificDiscounts.
    this.mostSpecificDiscounts = issue.mostSpecificDiscounts ?? [];

    this.updateStore(issue);
  }
}
